import React, { CSSProperties } from 'react';
import { RequestStatus, ScreenType } from "Constants";
import "./styles.sass";

// @ts-ignore
import { LocalizationContext } from "react-localize";
import { btnContent } from 'Generic/Buttons/ViewControllerBtns/VCButton/VCButton';
import { Transaction, TransactionHistoryType, TransactionType } from 'Interfaces/Transactions';
import VCBContainer from 'Generic/Buttons/ViewControllerBtns/VCBContainer';
import { MvSortTable } from 'Generic/MvSortTable';

// Utils
import { sortTransactions, SortDirection, TransactionsSortType } from 'Utils/SortTransactions';
import moment, { Moment } from 'moment';
import { MvConfirmationModal } from 'Generic/MvConfirmationModal/MvConfirmationModal';
import ContractToolTip from 'Interfaces/ContractToolTipInterface';
import { Tip } from 'Interfaces/TipInterface';
import ToolTip from 'Generic/ToolTip';
import { Position } from 'Generic/ToolTip/ToolTip';
import { MvTransactionFilter } from 'Generic/MvTransactionFilter';
import { TransactionFilter, TransactionFilters, UsageFilter } from 'Generic/MvTransactionFilter/types';
import LocalizationService from "Services/localization.service";

const defaultUseYears = [
    parseInt(moment().subtract(1, 'year').format('YYYY')),
    parseInt(moment().format('YYYY')),
    parseInt(moment().add(1, 'year').format('YYYY')),
    parseInt(moment().add(2, 'year').format('YYYY')),
];

const getDefaultSelectedFilters = (): TransactionFilters => {
    return {
        transactionYears: [],
        transactionTypes: [],
        contracts: [],
        usageYears: [...defaultUseYears],
        usageTypes: [],
        userName: [],
    };
};

const minWidth = 1080;
const maxWidth = 1600;
const minDescriptionWidth = 100;
const maxDescriptionWidth = 400;
const minDescriptionLength = 25;
const maxDescriptionLength = 120;
const descriptionLengthFactor = (maxWidth - minWidth) / (maxDescriptionLength - minDescriptionLength);
const descriptionWidthFactor = (maxWidth - minWidth) / (maxDescriptionWidth - minDescriptionWidth);

export type activeTransactionsSortField = {
    sortField: TransactionsSortType;
    sortDirection: SortDirection;
}

type Props = {
    transactions: Transaction[];
    filters: TransactionFilters;
    contractToolTips: ContractToolTip[];
    requestStatus: RequestStatus;
    activeSortField: activeTransactionsSortField|null;
    onTransactionsSort: (contracts: Transaction[], activeSortField: activeTransactionsSortField) => void;
    apiTimeout: string;
};

type State = {
    activeSortField: TransactionsSortType;
    SortFields: {
        [TransactionsSortType.ID]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.DATE]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.TYPE]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.CONTRACT]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.USAGE_YEAR]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.USAGE_TYPE]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
        [TransactionsSortType.AMOUNT]: {
            curSortDirection: SortDirection;
            defaultSortDirection: SortDirection;
        };
    };
    selectedFilters: TransactionFilters;
    windowWidth: number;
} 

const InitState: State = {
    activeSortField: TransactionsSortType.DATE,
    SortFields: {
        [TransactionsSortType.ID]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
        [TransactionsSortType.DATE]: {
            curSortDirection: SortDirection.DESC,
            defaultSortDirection: SortDirection.DESC,
        },
        [TransactionsSortType.TYPE]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
        [TransactionsSortType.CONTRACT]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
        [TransactionsSortType.USAGE_YEAR]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
        [TransactionsSortType.USAGE_TYPE]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
        [TransactionsSortType.AMOUNT]: {
            curSortDirection: SortDirection.ASC,
            defaultSortDirection: SortDirection.ASC,
        },
    },
    selectedFilters: getDefaultSelectedFilters(),
    windowWidth: 0,
}

class Transactions extends React.Component<Props, State> {
    localizationService: LocalizationService;
    _isMounted: boolean;

    constructor(props: Readonly<Props>) {
        super(props);

        this._isMounted = false;
        this.localizationService = new LocalizationService();
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
        this.state = Object.assign({}, InitState);
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.activeSortField) {
            const newState = {...this.state};
            const {activeSortField} = this.props;
            newState.activeSortField = activeSortField.sortField;
            newState.SortFields[activeSortField.sortField].curSortDirection = activeSortField.sortDirection;
            this.setState(newState);
        }
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount(): void {
        this._isMounted = false;
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    /**
     * return the array of headers for the Reservations table
     */
    tableHeaders() {
        const { localize } = this.context;
        return [
            {
                text: localize("static.content.transactions-transaction-id"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.ID].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.ID)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.ID,
            },
            {
                text: localize("static.content.transactions-transaction-date"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.DATE].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.DATE)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.DATE,
            },
            {
                text: localize("static.content.transactions-transaction-type"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.TYPE].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.TYPE)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.TYPE,
            },
            {
                text: localize("static.content.transactions-contract"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.CONTRACT].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.CONTRACT)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.CONTRACT,
            },
            {
                text: localize("static.content.transactions-use-year"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.USAGE_YEAR].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.USAGE_YEAR)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.USAGE_YEAR,
            },
            {
                text: localize("static.content.transactions-usage-type"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.USAGE_TYPE].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.USAGE_TYPE)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.USAGE_TYPE,
            },
            {
                text: localize("static.content.transactions-amount"),
                sortable: true,
                curSortDirection: this.state.SortFields[TransactionsSortType.AMOUNT].curSortDirection,
                onSort: () => {
                    this.onSortClicked(TransactionsSortType.AMOUNT)
                },
                activeSort: this.state.activeSortField === TransactionsSortType.AMOUNT,
            },
            {
                text: localize("static.content.transactions-description"),
                sortable: false,
            },
        ]
    }

    filterTransactions = (transactions: Transaction[]) => {
        const {
            transactionYears,
            transactionTypes,
            contracts,
            usageYears,
            usageTypes,
            // userName,
        } = this.state.selectedFilters;

        const filterPassedCheck = (filters: Array<string|number>, value: string|number): boolean => {
            if (filters.length === 0) return true;
            if (filters.includes(value)) return true;

            return false;
        };

        return transactions.filter(t => {
            return filterPassedCheck(transactionYears, this.getTransDateNoTime(t.date).get('year')) &&
                filterPassedCheck(transactionTypes, this.getFriendlyTransactionType(t.type)) &&
                filterPassedCheck(contracts, t.contractNumber) &&
                filterPassedCheck(usageYears, t.usageYear) &&
                filterPassedCheck(usageTypes, this.getFriendlyUsageType(t.usageType))
        });
    };

    getTransDateNoTime = (date: string|Date): Moment => {
        return moment(date.toString().split('T')[0]);
    };

    tableRows(noTableRows: boolean) {
        const { localize } = this.context;
        
        if (noTableRows) {
            return new Array({
                rowKey: "-1",
                rowCells: {
                    errorMessage: ( 
                        <div className="transactions-none">{localize("static.content.transactions-no-transactions")}</div>
                    )
                }
            });
        }
        else {
            return this.filterTransactions(this.props.transactions).map(transaction => {
                const { localize } = this.context;
                const transDate = this.getTransDateNoTime(transaction.date).format("MM/DD/YYYY");
                
                const rowCells:any = {
                    id: <p aria-label="transId">{transaction.id}</p>,
                    date: <p aria-label="date">{transDate}</p>,
                    type: <p aria-label="trans-type">{transaction.friendlyTransactionType}</p>,
                    contractId: this.getContractToolTip(transaction.contractNumber, transaction.id.toString()),
                    usageYear: <p aria-label="useYear">{transaction.usageYear}</p>,
                    usageType: <p aria-label="usageType" className="usageType-column">{transaction.friendlyUsageType}</p>,
                    amount: <p aria-label="amount">{transaction.amount}</p>,
                    description: this.getDescriptionJSX(transaction.description ? transaction.description : '', transaction.id),
                }

                return {
                    rowKey: transaction.id.toString(),
                    rowCells
                }
            })
        }
    }

    /**
     * fired when a column sort icon is clicked in the Transaction list table
     */
     onSortClicked(sortField: TransactionsSortType) {
        const transactions = this.props.transactions;
        const activeSortField = this.state.activeSortField;
        const sortTable = this.state.SortFields;

        let sortDirection = sortTable[sortField].curSortDirection;
        if (activeSortField === sortField) {
            sortDirection = sortTable[sortField].curSortDirection === SortDirection.ASC ?
                SortDirection.DESC : SortDirection.ASC;
        }
        else {
            // reset previous sort direction to default
            sortTable[activeSortField].curSortDirection = sortTable[activeSortField].defaultSortDirection;
        }

        console.log({onSortClicked_sortDirection:sortDirection, transactions:transactions})

        const sortedTransactions = sortTransactions(
            transactions,
            sortField,
            sortDirection
        );

        sortTable[sortField].curSortDirection = sortDirection;

        const newState = { ...this.state };
        //newState.transactions = sortedTransactions;
        newState.activeSortField = sortField;
        newState.SortFields = sortTable;
        this.setState(newState);

        //update transations state on parent
        const activeTransactionsSortField = {sortField:sortField,sortDirection:sortDirection}
        this.props.onTransactionsSort(sortedTransactions, activeTransactionsSortField);
    }

    /**
     * Gets the ContractToolTip for the specific contractId
     * If it can't be found, return the generic ToolTip
     */
    getContractToolTip(contractId: string, transId: string): any {
        const { contractToolTips } = this.props;

        const contractToolTip = contractToolTips === null ? null : contractToolTips.find((ctt: ContractToolTip) => {
        return ctt.contractId === contractId;
        });

        return contractToolTip ? contractToolTip.toolTip : this.getContractToolTipGeneric(contractId, transId);
    }

    /**
     * Gets the ToolTip for the specific contractId
     */
    getContractToolTipGeneric(contractId: string, transId: string): any {
        const { localize } = this.context;
        const tip: [Tip] = [{ label: `${localize("static.content.ayp-contract")}`, desc: `${contractId}` }];
        const contractIdStyle = <p key={transId} className="contract-id-link mv-print-link" aria-label={contractId}>{contractId}</p>;

        return (
            <ToolTip
                id={`contract-generic-${contractId}`}
                tabIndex={-1}
                tip={tip}
                useRefToPosition
            >
                {contractIdStyle}
            </ToolTip>
        );
    }

    getFriendlyTransactionType = (transactionType: string) => {
        const friendlyName = this.localizationService.getTransactionTypeNameFromJahia(transactionType);
        return friendlyName.value || friendlyName.key;
    };  

    getFriendlyUsageType = (usageType: string) => {
        return this.localizationService.getUsageTypeNameFromJahia(usageType).value;
    };  

    getDescriptionJSX(description: string, transId: number) {
        const tip: [Tip] = [{ label: '', desc: `${description}` }];
        let shortDesc = description;

        const windowWidth = this.state.windowWidth;
        let descriptionLength = maxDescriptionLength;
        if (windowWidth <= maxWidth){
            descriptionLength = maxDescriptionLength - ((maxWidth - windowWidth) / descriptionLengthFactor)
        }
        if (descriptionLength < minDescriptionLength)
            descriptionLength = minDescriptionLength;

        if (description.length > descriptionLength) {
            shortDesc = description.substring(0, descriptionLength) + '...';
            const descriptionStyle = <p key={transId} className="description-link mv-print-link" aria-label={description}>{shortDesc}</p>;

            return <div className="tool-tip-trans-description">
                <ToolTip useRefToPosition={true} id={`trans-description-generic-${transId}`} tabIndex={-1} tip={tip} position={Position.CENTER}>
                    {descriptionStyle}
                </ToolTip>
            </div>
        }
        else {

            return <>

                <p aria-label={description}>{description}</p>

            </>

        }
    }

    onFilterChange = (filterType: TransactionFilter|UsageFilter, selection: string|number) => {
        let selectedFilters = this.state.selectedFilters;
        let selectedFiltersInSection = selectedFilters[filterType];

        if (selectedFiltersInSection.includes(selection as never)) {
            selectedFiltersInSection = selectedFiltersInSection.filter(f => f !== selection)
        } else {
            selectedFiltersInSection.push(selection);
        }

        selectedFilters = {
            ...selectedFilters,
            [filterType]: selectedFiltersInSection,
        };

        this.setState({ selectedFilters });
    };

    updateWindowDimensions() {
        this.setState({ windowWidth: window.innerWidth });
    }

    getColumnStyles(noTableRows: boolean) {
        const windowWidth = this.state.windowWidth;
        let descriptionWidth = maxDescriptionWidth;
        if (windowWidth <= maxWidth){
            descriptionWidth = maxDescriptionWidth - ((maxWidth - windowWidth) / descriptionWidthFactor)
        }
        if (descriptionWidth < minDescriptionWidth)
            descriptionWidth = minDescriptionWidth;
        
        let columnStyles: {[key: string]: CSSProperties} = {
            0: {
                'flex': noTableRows ? '0 0 1' : ''
            },
            // 7: {
            //     'flex': '0 0 ' + descriptionWidth + 'px',
            // }
        }
        if (this.state.windowWidth < minWidth) {
            columnStyles = {
                0: {
                    'flex': noTableRows ? '0 0 1' : '0 0 120px',
                },
                1: {
                    'flex': '0 0 130px',
                },
                2: {
                    'flex': '0 0 130px',
                },
                3: {
                    'flex': '0 0 110px',
                },
                4: {
                    'flex': '0 0 100px',
                },
                5: {
                    'flex': '0 0 170px',
                },
                6: {
                    'flex': '0 0 100px',
                },
                7: {
                    'flex': ''
                }
            }
        }
        return columnStyles;
    }

    render() {
        const { localize } = this.context;
        const noTableRows = !this.props.transactions || ((this.props.transactions.length === 0 || this.filterTransactions(this.props.transactions).length === 0)
        && this.props.requestStatus === RequestStatus.LOADED);

        return (
            <div className="transactions-container">
                <MvSortTable
                    rowKey="transId"
                    headers={this.tableHeaders()}
                    rows={this.tableRows(noTableRows)}
                    rowsExpandable={false}
                    dataStatus={this.props.requestStatus}
                    headerSection={
                    <div className="transactions-header">
                        <div className="table-info">
                            <h4>
                                {localize("static.content.table-header-bytransaction")}
                            </h4>
                            <span className="transactions-subheader">
                                {localize("static.content.table-subheader-bytransaction")}
                            </span>
                        </div>
                        <div className="filter-container">
                            <MvTransactionFilter
                                mode='transaction'
                                filters={this.props.filters}
                                onFilterChange={this.onFilterChange}
                                onReset={() => {
                                    this.setState({
                                        selectedFilters: getDefaultSelectedFilters(),
                                    });
                                }}
                                selected={this.state.selectedFilters}
                            />
                        </div>
                    </div>
                    }
                    minWidth={this.state.windowWidth < minWidth ? minWidth : 0}
                    columnStyles={this.getColumnStyles(noTableRows)}
                    apiTimeout = {this.props.apiTimeout}
                />
            </div>
        );
    }

};

Transactions.contextType = LocalizationContext;
export {
    Transactions,
};